import { useState, useCallback, useEffect } from 'react';
import { BENEFIT_OPTIONS } from '../constants/formInitialStates';

/**
 * Custom hook for managing company form submission logic
 * Encapsulates form data building, API calls, photo uploads, and error handling
 */
export const useCompanyForm = ({ 
  initialState, 
  initialValues, 
  restaurantId, 
  onFormSubmit, 
  setUserInfo 
}) => {
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState(initialState);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [logoFile, setLogoFile] = useState(null);

  // Helper function to normalize benefits
  const normalizeBenefits = useCallback((benefits) => {
    return (benefits || []).reduce((acc, benefit) => {
      const benefitName = typeof benefit === 'string' && /^\d+$/.test(benefit) 
        ? BENEFIT_OPTIONS[parseInt(benefit)]?.key 
        : benefit;
      
      if (benefitName) {
        acc[benefitName] = true;
      }
      return acc;
    }, {});
  }, []);

  // Helper function to process image URL
  const processInitialImageUrl = useCallback((imageUrl) => {
    return Array.isArray(imageUrl) ? imageUrl[0] || '' : imageUrl || '';
  }, []);

  // Helper function to process carousel URLs
  const processInitialCarouselUrls = useCallback((urls) => {
    return Array.isArray(urls) ? urls : [];
  }, []);

  // Helper function to process locations
  const processInitialLocations = useCallback((locations) => {
    return Array.isArray(locations) && locations.length > 0
      ? locations
      : [{ address: "", latitude: "", longitude: "" }];
  }, []);

  // Helper function to build initial state
  const buildInitialState = useCallback((prevState, initialValues) => {
    return {
      ...prevState,
      ...initialValues,
      benefits: {
        ...prevState.benefits,
        ...normalizeBenefits(initialValues.benefits),
      },
      profileImageUrl: processInitialImageUrl(initialValues.profileImageUrl),
      profileCarouselUrls: processInitialCarouselUrls(initialValues.profileCarouselUrls),
      locations: processInitialLocations(initialValues.locations)
    };
  }, [normalizeBenefits, processInitialImageUrl, processInitialCarouselUrls, processInitialLocations]);

  // Initialize form with initial values
  useEffect(() => {
    if (initialValues) {
      setState(prevState => buildInitialState(prevState, initialValues));
    }
  }, [initialValues, buildInitialState]);

  // Helper function to process benefits
  const processBenefits = useCallback((benefits) => {
    return Object.keys(benefits).filter(benefit => benefits[benefit]);
  }, []);

  // Helper function to process carousel URLs
  const processCarouselUrls = useCallback((urls) => {
    return urls.filter(url => !url.startsWith('blob:'));
  }, []);

  // Helper function to process image URL
  const processImageUrl = useCallback((imageUrl) => {
    return Array.isArray(imageUrl) ? imageUrl[0] || '' : imageUrl || '';
  }, []);

  // Helper function to add form fields to FormData
  const addFormFields = useCallback((formData) => {
    Object.keys(state).forEach(key => {
      switch (key) {
        case 'benefits':
          formData.append(key, JSON.stringify(processBenefits(state.benefits)));
          break;
        case 'locations':
          formData.append(key, JSON.stringify(state.locations));
          break;
        case 'profileCarouselUrls':
          formData.append(key, JSON.stringify(processCarouselUrls(state.profileCarouselUrls)));
          break;
        case 'profileImageUrl':
          if (!logoFile) {
            formData.append(key, processImageUrl(state[key]));
          }
          break;
        default:
          formData.append(key, state[key]);
      }
    });
  }, [state, logoFile, processBenefits, processCarouselUrls, processImageUrl]);

  // Helper function to add files to FormData
  const addFiles = useCallback((formData) => {
    if (logoFile) {
      formData.append('profileImage', logoFile);
    } else if (state.profileImageUrl && !state.profileImageUrl.startsWith('blob:')) {
      const imageUrl = processImageUrl(state.profileImageUrl);
      if (imageUrl) formData.append('profileImageUrl', imageUrl);
    }

    galleryFiles.forEach(file => formData.append('galleryImages', file));
  }, [logoFile, state.profileImageUrl, galleryFiles, processImageUrl]);

  // Helper function to build FormData
  const buildFormData = useCallback(() => {
    const formData = new FormData();
    addFormFields(formData);
    addFiles(formData);
    return formData;
  }, [addFormFields, addFiles]);

  // Helper function to get API endpoint and method
  const getApiConfig = useCallback(() => {
    if (initialValues && initialValues.id) {
      // If we have initialValues, we're editing an existing restaurant
      // restaurantId should always be provided for updates
      if (!restaurantId) {
        throw new Error('restaurantId is required for updating existing restaurants');
      }
      return {
        endpoint: `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/company?restaurantId=${restaurantId}`,
        method: 'PATCH'
      };
    }
    // Creating new restaurant
    return {
      endpoint: `${process.env.REACT_APP_BACKEND_STAGING_URL}/api/company`,
      method: 'POST'
    };
  }, [initialValues, restaurantId]);

  // Helper function to handle authentication refresh
  const refreshAuthState = useCallback(async () => {
    try {
      const authResponse = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/check-login-status`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (authResponse.ok) {
        const authData = await authResponse.json();
        if (authData.isLoggedIn && authData.user) {
          setUserInfo({
            ...authData.user,
            isAuthenticated: true,
          });
        }
      }
    } catch (authError) {
      console.error('❌ Error refreshing authentication state:', authError);
    }
  }, [setUserInfo]);

  // Helper function to handle specific error cases
  const handleError = useCallback((error) => {
    if (error.message?.includes('ubicaciones')) {
      alert(`Error de límite de ubicaciones: ${error.message}\n\nPara agregar más ubicaciones, actualiza tu plan en la configuración de tu cuenta.`);
      return;
    }
    
    if (error.message?.includes('plan')) {
      alert(`Error de plan: ${error.message}\n\nPara acceder a esta función, actualiza tu plan en la configuración de tu cuenta.`);
      return;
    }
  }, []);

  // Main submit handler
  const handleSubmit = useCallback(async (validateFields) => {
    // Validate form
    const validationErrors = validateFields(state);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }

    setUploading(true);
    setErrors({});

    try {
      const { endpoint, method } = getApiConfig();
      const formData = buildFormData();

      const response = await fetch(endpoint, {
        method,
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Refresh auth state for new companies
      if (method === 'POST') {
        await refreshAuthState();
      }
      
      onFormSubmit();
      return true;

    } catch (error) {
      console.error('💥 Error in handleSubmit:', error);
      handleError(error);
      return false;
    } finally {
      setUploading(false);
    }
  }, [getApiConfig, buildFormData, refreshAuthState, handleError, onFormSubmit]);

  // Image dimension validation constants
  // Based on display requirements: form preview 8.5rem (~136px), profile header 12rem (~192px)
  // For Retina displays (2x): 384px needed. Minimum 300px covers 1.5x Retina
  const IMAGE_CONSTRAINTS = {
    minWidth: 300,
    minHeight: 300,
    maxWidth: 3000,
    maxHeight: 3000,
    minAspectRatio: 0.8, // Allow slightly rectangular (e.g., 300x375px)
    maxAspectRatio: 1.25, // But prefer square for circular display (e.g., 375x300px)
  };

  // Helper function to validate file
  const isValidFile = useCallback((file) => {
    return file && file instanceof File && file.size > 0;
  }, []);

  // Helper: Validate image dimensions (warning only, doesn't block submission)
  const validateImageDimensions = useCallback((width, height) => {
    const { minWidth, minHeight, maxWidth, maxHeight, minAspectRatio, maxAspectRatio } = IMAGE_CONSTRAINTS;
    const aspectRatio = width / height;

    if (width < minWidth || height < minHeight) {
      return `⚠️ Precaución: La imagen es muy pequeña. Se recomienda dimensiones mínimas: ${minWidth}x${minHeight}px. Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    if (width > maxWidth || height > maxHeight) {
      return `⚠️ Precaución: La imagen es muy grande. Se recomienda dimensiones máximas: ${maxWidth}x${maxHeight}px. Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    if (aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio) {
      return `⚠️ Precaución: Se recomienda que la imagen sea aproximadamente cuadrada (relación de aspecto entre ${minAspectRatio} y ${maxAspectRatio}). Tu imagen: ${width}x${height}px. Puedes continuar de todas formas.`;
    }

    return null; // Validation passed
  }, []);

  // Helper: Clear image errors
  const clearImageErrors = useCallback(() => {
    setErrors((prevErrors) => {
      const { profileImage, ...rest } = prevErrors;
      return rest;
    });
  }, []);

  // Helper: Handle image error
  const handleImageError = useCallback((errorMessage) => {
    setErrors((prevErrors) => ({ ...prevErrors, profileImage: errorMessage }));
    setState((prevState) => ({ ...prevState, profileImageUrl: '' }));
    setLogoFile(null);
  }, []);

  // Photo upload handlers
  const handleLogoUpload = useCallback((file) => {
    if (!isValidFile(file)) {
      console.error('❌ Invalid file provided to handleLogoUpload:', file);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      handleImageError('Por favor selecciona un archivo de imagen válido.');
      return;
    }

    // Validate image dimensions (warning only, doesn't block)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      const validationError = validateImageDimensions(img.width, img.height);
      
      // Always set the preview and file, even if dimensions don't meet requirements
      setLogoFile(file);
      setState(prevState => ({
        ...prevState,
        profileImageUrl: objectUrl
      }));
      
      // Show warning if dimensions don't meet requirements, but don't block
      if (validationError) {
        setErrors((prevErrors) => ({ ...prevErrors, profileImage: validationError }));
      } else {
        clearImageErrors();
      }
    };

    img.onerror = () => {
      handleImageError('Error al cargar la imagen. Por favor intenta con otro archivo.');
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  }, [isValidFile, validateImageDimensions, clearImageErrors, handleImageError]);

  const handleCarouselPhotoUpload = useCallback((files) => {
    
    if (!Array.isArray(files) || files.length === 0) {
      return;
    }

    const validFiles = files.filter(isValidFile);
    
    if (validFiles.length === 0) {
      return;
    }

    setGalleryFiles(prevFiles => [...prevFiles, ...validFiles]);
    
    try {
      const imageUrls = validFiles.map(file => URL.createObjectURL(file));
      
      setState(prevState => {
        const newUrls = [...prevState.profileCarouselUrls, ...imageUrls];
        return {
          ...prevState,
          profileCarouselUrls: newUrls
        };
      });
    } catch (error) {
      console.error('❌ Error creating object URLs for gallery:', error);
    }
  }, [isValidFile, state.profileCarouselUrls]);

  const handleRemoveGalleryPhoto = useCallback((index) => {
    setState(prevState => ({
      ...prevState,
      profileCarouselUrls: prevState.profileCarouselUrls.filter((_, i) => i !== index)
    }));
    setGalleryFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  }, []);

  return {
    // State
    state,
    setState,
    uploading,
    errors,
    galleryFiles,
    setGalleryFiles,
    logoFile,
    
    // Handlers
    setErrors,
    handleSubmit,
    handleLogoUpload,
    handleCarouselPhotoUpload,
    handleRemoveGalleryPhoto
  };
};
